import { Button } from "@/components/ui/button";
import { Plus, Database, Upload, CopyCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useFeatureFlag } from "@/providers/FeatureFlagsProvider";
import { useSettings } from "@/providers/SettingsProvider";
import { generateBelgianCandidateData } from "@/utils/candidate/operations";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";

interface CandidatesActionsProps {
  isGenerating: boolean;
  setIsGenerating: (isGenerating: boolean) => void;
  fetchCandidates: () => Promise<void>;
  onAddCandidate: () => void;
  onBulkImport: () => void;
}

export const CandidatesActions = ({
  isGenerating,
  setIsGenerating,
  fetchCandidates,
  onAddCandidate,
  onBulkImport
}: CandidatesActionsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const duplicateFinderEnabled = useFeatureFlag("duplicateFinder");
  // Use try-catch to handle the case where SettingsProvider is not available
  let devMode = false;
  try {
    const { devMode: settingsDevMode } = useSettings();
    devMode = settingsDevMode;
  } catch (error) {
    console.error("SettingsProvider not available:", error);
    // Continue with default value for devMode
  }

  const handleGenerateDummyData = async () => {
    try {
      setIsGenerating(true);
      await generateBelgianCandidateData();
      // Refresh candidates list after generating data
      await fetchCandidates();
    } catch (error) {
      console.error("Failed to generate candidate data:", error);
      toast({
        title: t("candidates.actions.errorTitle"),
        description: t("candidates.actions.errorGenerate"),
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="flex gap-2">
      {devMode && (
        <Button 
          variant="outline" 
          onClick={handleGenerateDummyData}
          disabled={isGenerating}
          className="flex items-center gap-2"
        >
          <Database className="h-4 w-4" />
          {isGenerating ? t("candidates.actions.generating") : t("candidates.actions.generateDummy")}
        </Button>
      )}
      {duplicateFinderEnabled && (
        <Button
          variant="outline"
          onClick={() => navigate("/candidates/duplicates")}
          className="flex items-center gap-2"
        >
          <CopyCheck className="h-4 w-4" />
          {t("candidates.duplicateFinder.button")}
        </Button>
      )}
      <Button
        variant="outline"
        onClick={onBulkImport}
        className="flex items-center gap-2"
        data-tour="candidates-bulk-import"
      >
        <Upload className="h-4 w-4" />
        {t("candidates.actions.bulkImport", "Bulk Import")}
      </Button>
      <Button
        onClick={onAddCandidate}
        className="bg-[#5a7ebf] hover:bg-[#5a7ebf]/90"
        data-tour="candidates-add"
      >
        <Plus className="h-4 w-4 mr-2" />
        {t("candidates.actions.newCandidate")}
      </Button>
    </div>
  );
};
